import { LIST_OF_ISO_CODES } from "@kazaar-platform/design-system";
import { isEmpty, sumBy, trim } from "lodash";
import {
  type OrderGetOrderDetailsOrderItemReadDto,
  type OrderItemDetailsReadDto,
  OrderItemUpdateCommandDispatchTypeEnum,
} from "@/config/apiClient";
import { z } from "@/helpers/forms/zod";

export const orderItemConfigurationSchema = z.object({
  clientReference: z.string().optional(),
  configIds: z.array(z.string().uuid()).optional(),
  name: z.string(),
  specificRequest: z.string().max(5000).optional(),
  deliveryDate: z.date().optional(),
  totalQuantity: z.number().min(0),
  withPreMedia: z.boolean(),
  dispatchType: z.enum(OrderItemUpdateCommandDispatchTypeEnum),
  dispatchFiles: z
    .array(
      z.object({
        fileSignedId: z.string(),
        blobId: z.string().optional(),
        isDeleted: z.boolean().optional(),
        fileName: z.string().optional(),
        size: z.number().optional(),
        url: z.string().optional(),
      })
    )
    .optional(),
  versions: z.array(
    z.object({
      name: z.string().min(1),
      fileSignedId: z.string().optional(),
      isHDFileDeleted: z.boolean().optional(),
      blobId: z.string().optional(),
      id: z.string().optional(),
      tempId: z.string().optional(),
      isDeleted: z.boolean().optional(),
      fileName: z.string().optional(),
      size: z.number().optional(),
      url: z.string().optional(),
      dispatches: z.array(
        z.object({
          quantity: z.number(),
          address: z.object({
            id: z.string().nullable().optional(),
            name: z.string(),
            recipientName: z.string().nullable().optional(),
            address: z.string(),
            complementaryInfo: z.string().nullable().optional(),
            postalCode: z.string(),
            city: z.string(),
            country: z.string(),
            phoneCountry: z.enum(LIST_OF_ISO_CODES).nullable().optional(),
            phoneNumber: z
              .string()
              .transform((value) => trim(value))
              .nullable()
              .optional(),
          }),
        })
      ),
    })
  ),
  preMediaBrief: z.string().optional(),
  preMediaFiles: z
    .array(
      z.object({
        fileSignedId: z.string(),
        blobId: z.string().optional(),
        isDeleted: z.boolean().optional(),
        fileName: z.string().optional(),
        size: z.number().optional(),
        url: z.string().optional(),
      })
    )
    .optional(),
});

export type OrderItemConfigurationSchema = z.infer<
  typeof orderItemConfigurationSchema
>;

export const getDefaultValues = ({
  orderItem,
}: {
  orderItem: OrderGetOrderDetailsOrderItemReadDto | OrderItemDetailsReadDto;
}): OrderItemConfigurationSchema => {
  return {
    clientReference: orderItem.clientReference ?? "",
    specificRequest: orderItem.sandboxItem.specificRequest ?? undefined,
    name: orderItem.sandboxItem.name ?? "",
    configIds: orderItem.sandboxItem.SandboxItemToConfiguratorOptions.map(
      (item) => item.configuratorOptionId
    ),
    deliveryDate: orderItem.deliveryDate
      ? new Date(orderItem.deliveryDate)
      : undefined,
    withPreMedia: orderItem.sandboxItem.withPreMedia ?? false,
    totalQuantity: sumBy(orderItem.sandboxItem.SandboxItemVersion, (version) =>
      sumBy(version.SandboxItemVersionDispatch, "quantity")
    ),
    dispatchType:
      orderItem.sandboxItem.dispatchType ??
      OrderItemUpdateCommandDispatchTypeEnum.SingleDispatch,
    dispatchFiles: orderItem.sandboxItem.dispatchFiles
      ? orderItem.sandboxItem.dispatchFiles.map((dispacthFile) => ({
          fileSignedId: "",
          isDeleted: false,
          ...dispacthFile,
        }))
      : [
          {
            fileSignedId: "",
          },
        ],
    versions: orderItem.sandboxItem.SandboxItemVersion.map((version) => ({
      name: version.name,
      blobId: version.file?.blobId ?? undefined,
      id: version.id,
      fileSignedId: "",
      isDeleted: false,
      isHDFileDeleted: false,
      fileName: version.file?.fileName ?? undefined,
      size: version.file?.size ?? undefined,
      url: version.file?.url ?? undefined,
      dispatches: version.SandboxItemVersionDispatch.map((dispatch) => ({
        quantity: dispatch.quantity,
        address: {
          id: dispatch.id,
          name: dispatch.nameAddress ?? "",
          recipientName: dispatch.recipientName,
          address: dispatch.address,
          complementaryInfo: dispatch.complementaryInfo,
          postalCode: dispatch.postalCode,
          city: dispatch.city,
          country: dispatch.country,
          phoneNumber: dispatch.phoneNumber,
        },
      })),
    })),
    preMediaBrief: orderItem.sandboxItem.preMediaBrief ?? "",
    preMediaFiles: !isEmpty(orderItem.sandboxItem.preMediaFiles)
      ? orderItem.sandboxItem.preMediaFiles
          ?.map((preMediaFile) => ({
            fileSignedId: "",
            blobId: preMediaFile.blobId,
            isDeleted: false,
            fileName: preMediaFile.fileName,
            size: preMediaFile.size,
            url: preMediaFile.url,
          }))
          ?.concat([
            {
              fileSignedId: "",
              isDeleted: false,
              blobId: "",
              fileName: "",
              size: undefined,
              url: "",
            },
          ])
      : [{ fileSignedId: "" }],
  } as const satisfies OrderItemConfigurationSchema;
};
